import styled from "styled-components";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LogoImg from "../../images/LogoImg.png";
import { Bio } from "../../data/constants";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";

const FooterShell = styled.footer`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 24px 32px;
  background:
    radial-gradient(circle at top left, rgba(46, 184, 92, 0.22), transparent 32%),
    linear-gradient(135deg, #28ac30 0%, #0c7c20 100%);
  color: #f7fff8;
`;

const FooterInner = styled.div`
  width: 100%;
  max-width: 1200px;
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) repeat(3, minmax(0, 1fr));
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const BrandBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const BrandMark = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  width: fit-content;
  color: inherit;
  text-decoration: none;
`;

const Logo = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 16px;
  background: rgba(255, 255, 255);
  padding: 6px;
`;

const BrandName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CompanyName = styled.span`
  font-size: 1.18rem;
  font-weight: 700;
  line-height: 1.2;
`;

const BrandTagline = styled.span`
  color: rgba(247, 255, 248, 0.78);
  font-size: 0.95rem;
  line-height: 1.45;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ColumnTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #ffffff;
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FooterLink = styled(Link)`
  width: fit-content;
  color: rgba(247, 255, 248, 0.8);
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: #ffffff;
    transform: translateX(2px);
  }
`;

const FooterHashLink = styled(HashLink)`
  width: fit-content;
  color: rgba(247, 255, 248, 0.8);
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: #ffffff;
    transform: translateX(2px);
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContactLink = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: rgba(247, 255, 248, 0.82);
  text-decoration: none;
  line-height: 1.55;
  transition: color 0.2s ease;

  svg {
    font-size: 1.1rem;
    margin-top: 2px;
    color: #9bffb0;
    flex-shrink: 0;
  }

  &:hover {
    color: #ffffff;
  }
`;

const ContactText = styled.span`
  display: inline-block;
`;

const BottomRow = styled.div`
  margin-top: 36px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Legal = styled.p`
  margin: 0;
  color: rgba(224, 255, 230, 0.72);
  font-size: 0.9rem;
`;

const Socials = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(154, 255, 176, 0.45);
  }
`;

function Footer() {
  const year = new Date().getFullYear();

  return (
    <FooterShell>
      <FooterInner>
        <TopRow>
          <BrandBlock>
            <BrandMark to="/">
              <Logo src={LogoImg} alt="Mags Engineering Limited" />
              <BrandName>
                <CompanyName>Mags Engineering Limited</CompanyName>
                <BrandTagline>Setting the standards for engineering</BrandTagline>
              </BrandName>
            </BrandMark>
          </BrandBlock>

          <Column>
            <ColumnTitle>Explore</ColumnTitle>
            <LinkList>
              <FooterHashLink smooth to="/#about">About</FooterHashLink>
              <FooterLink to="/projects">Projects</FooterLink>
              <FooterHashLink smooth to="/#services">Services</FooterHashLink>
              <FooterLink to="/training">Training</FooterLink>
              <FooterLink to="/training-schedule">Training Schedule</FooterLink>
              <FooterLink to="/events">Events & Webinars</FooterLink>
              <FooterLink to="/forum">Forum</FooterLink>
              <FooterHashLink smooth to="/#testimonials">Testimonials</FooterHashLink>
            </LinkList>
          </Column>

          <Column>
            <ColumnTitle>Policies</ColumnTitle>
            <LinkList>
              <FooterLink to="/T&C">Terms & Conditions</FooterLink>
              <FooterLink to="/Privacy">Privacy Policy</FooterLink>
              <FooterLink to="/Refund">Refund Policy</FooterLink>
            </LinkList>
          </Column>

          <Column>
            <ColumnTitle>Contact</ColumnTitle>
            <ContactList>
              <ContactLink href="tel:+2348156358138">
                <PhoneIcon />
                <ContactText>+234 815 635 8138</ContactText>
              </ContactLink>
              <ContactLink href="mailto:contact@magsengineeringlimited.com">
                <EmailIcon />
                <ContactText>contact@magsengineeringlimited.com</ContactText>
              </ContactLink>
              <ContactLink href={Bio.whatsapp} target="_blank" rel="noreferrer">
                <WhatsAppIcon />
                <ContactText>Chat on WhatsApp</ContactText>
              </ContactLink>
              <ContactLink
                href="https://maps.google.com/?q=10%20Wals%20Close%20Trans-Amadi%20Industrial%20Layout%20Rivers%20State%20Nigeria"
                target="_blank"
                rel="noreferrer"
              >
                <LocationOnIcon />
                <ContactText>
                  10, Wals Close, Trans-Amadi Industrial Layout, Rivers State, Nigeria.
                </ContactText>
              </ContactLink>
            </ContactList>
          </Column>
        </TopRow>

        <BottomRow>
          <Legal>&copy; {year} Mags Engineering Limited. All rights reserved.</Legal>
          <Socials>
            <SocialLink href={Bio.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <LinkedInIcon />
            </SocialLink>
            <SocialLink href={Bio.facebook} target="_blank" rel="noreferrer" aria-label="Facebook">
              <FacebookIcon />
            </SocialLink>
            <SocialLink href={Bio.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp">
              <WhatsAppIcon />
            </SocialLink>
          </Socials>
        </BottomRow>
      </FooterInner>
    </FooterShell>
  );
}

export default Footer;
